import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';
import { getOrgContacts } from '../../../contacts.js';

export const addPeopleMixin = {

    //********************* GET ADDABLE PEOPLE ********************************//
    // Candidates for the "Add people" picker: the active user's sharing-group
    // members, minus anyone already in excludeIds (current participants).
    // * excludeIds (Array<string|number>) — ids to leave out (current participants)
    async getAddablePeople(excludeIds) {
        return getOrgContacts(this.activeUserId, excludeIds);
    },

    //********************* RENDER ADD MEMBERS ROW ********************************//
    // Markup for a single selectable row in the "Add people" picker.
    // * user (Object) — { user_id, fullname, avatar_128, avatar_512 }
    renderAddMembersRow(user) {
        const avatarUrl = user.avatar_128 || user.avatar_512;

        return /*html*/`
            <div class="add_members_row" ng-click="toggleUser('${user.user_id}')" ng-class="{'add_members_row--selected': isSelected('${user.user_id}')}" ng-show="matchesSearch('${user.user_id}')">
                <gh-avatar-webcomponent name="${user.fullname}"${avatarUrl ? ` url="${avatarUrl}"` : ''}></gh-avatar-webcomponent>
                <span class="add_members_row__name">${user.fullname}</span>
                <span class="add_members_row__checkbox">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                </span>
            </div>
        `;
    },

    //********************* ADD PEOPLE ********************************//
    // "Add people" button in the channel/item-thread header. Opens a GhDialog with
    // a Slack-style checkbox picker over the active user's sharing-group members
    // (minus existing participants), and adds the checked ones via
    // POST .../participants.
    async addPeople(button) {
        button?.classList.add('--loading');

        try {
            await this.showAddPeopleDialog();
        } finally {
            button?.classList.remove('--loading');
        }
    },

    //********************* SHOW ADD PEOPLE DIALOG ********************************//
    // Builds the addable-people list and opens the GhDialog picker. Split out of
    // addPeople() so the button's loading state covers exactly the
    // getAddablePeople() fetch, which is the slow part.
    async showAddPeopleDialog() {
        const self = this;
        const isItemThread = this.viewMode === 'thread' && this.threadState?.kind === 'item';
        const target = isItemThread ? this.threadState : this.channelState;
        const targetName = isItemThread ? target.itemTitle : target.name;
        const label = isItemThread ? 'participant' : 'member';
        const ghDialog = gudhub.ghconstructor.angularInjector.get('GhDialog');

        const availableUsers = await this.getAddablePeople(target.participants);
        const usersHtml = availableUsers.length
            ? availableUsers.map(user => this.renderAddMembersRow(user)).join('')
            : `<div class="add_members_empty">No people available to add</div>`;

        ghDialog.show({
            position: 'center',
            mobile_position: 'bottom_mobile',
            class: 'webhook_set_dialog add_members_dialog',
            toolbar: false,
            template: {
                toolbar: '',
                content: /*html*/`
                <div class="dialog_wrapper">
                    <span class="add_members_close" ng-click="hide()">&times;</span>
                    <span class="gh_element_name">${targetName}</span>
                    <span class="add_members_subtitle">${isItemThread ? 'Add Participants' : 'Add Members'}</span>

                    <div class="add_people_pending" ng-if="pendingUsers.length">
                        <span ng-repeat="user in pendingUsers" class="add_people_chip">
                            {{user.fullname}}
                            <span ng-click="toggleUser(user.user_id)" class="add_people_chip__remove">&times;</span>
                        </span>
                    </div>

                    ${availableUsers.length ? /*html*/`
                    <div class="add_members_search">
                        <input type="text" class="disable-gudhub-styles" ng-model="searchQuery" placeholder="Search people">
                    </div>
                    ` : ''}

                    <div class="add_members_list">
                        ${usersHtml}
                        ${availableUsers.length ? `<div class="add_members_empty" ng-show="!hasMatches()">No matching people</div>` : ''}
                    </div>

                    <span ng-if="showErrorMessage" class="error"> Pick at least one person to add </span>

                    <button class="btn save_btn btn-blue add_people_submit_btn" ng-click="addPeopleToChannel()">Add people</button>

                    <div ng-if="showLoader" class="create_group_loader loader">
                        <span></span>
                    </div>
                </div>
                `
            },

            controller: ['$scope', function($scope) {
                $scope.pendingUsers = [];
                $scope.searchQuery = '';

                const usersById = new Map(availableUsers.map(user => [String(user.user_id), user]));

                $scope.matchesSearch = (userId) => {
                    if(!$scope.searchQuery) return true;
                    const user = usersById.get(String(userId));
                    return (user.fullname || '').toLowerCase().includes($scope.searchQuery.toLowerCase());
                };

                $scope.hasMatches = () => availableUsers.some(user => $scope.matchesSearch(user.user_id));

                $scope.isSelected = (userId) => $scope.pendingUsers.some(user => String(user.user_id) === String(userId));

                $scope.toggleUser = (userId) => {
                    userId = String(userId);

                    if($scope.isSelected(userId)) {
                        $scope.pendingUsers = $scope.pendingUsers.filter(user => String(user.user_id) !== userId);
                    } else {
                        $scope.pendingUsers = [...$scope.pendingUsers, usersById.get(userId)];
                    }
                };

                $scope.addPeopleToChannel = async () => {
                    $scope.showErrorMessage = false;

                    if(!$scope.pendingUsers.length) {
                        $scope.showErrorMessage = true;
                        return;
                    }

                    $scope.showLoader = true;

                    for(const user of $scope.pendingUsers) {
                        await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${target.conversationId}/participants`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ user_id: user.user_id, action: 'add', actor_id: self.activeUserId })
                        });
                    }

                    await self.prefetchParticipants($scope.pendingUsers.map(user => user.user_id));
                    target.participants.push(...$scope.pendingUsers.map(user => user.user_id));
                    self.renderHeaderSubtitle(target.participants, label);

                    $scope.showLoader = false;
                    $scope.hide();
                };
            }]
        });
    },
};
